import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useUI } from '../context/UIContext';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import LocationCityOutlined from '@mui/icons-material/LocationCityOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';

const SECTION_LABEL_STYLE = {
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 500,
  fontSize: '11px',
  color: '#565656',
  letterSpacing: '2px',
  padding: '0 24px 8px 24px',
};

const DIVIDER_STYLE = {
  borderTop: '1px solid #565656',
  margin: '16px 0',
};

const STATUS_COLORS = {
  'Proposal':  { bg: 'rgba(229,192,75,0.1)',  color: '#E5C04B' },
  'Active':    { bg: 'rgba(57,255,20,0.1)',   color: '#39FF14' },
  'On Hold':   { bg: 'rgba(229,192,75,0.1)',  color: '#E5C04B' },
  'Completed': { bg: 'rgba(83,145,255,0.1)',  color: '#5391FF' },
  'Archived':  { bg: 'rgba(86,86,86,0.2)',    color: '#565656' },
};

function NavItem({ label, Icon, to }) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  return (
    <div
      onClick={() => to && navigate(to)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '11px 24px',
        color: hovered ? '#39FF14' : '#F5F5F5',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '15px',
        cursor: 'pointer',
        backgroundColor: hovered ? 'rgba(57, 255, 20, 0.06)' : 'transparent',
        transition: 'all 0.15s ease',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon style={{ fontSize: 18, color: 'inherit' }} />
      {label}
    </div>
  );
}

const EVENT_STATUS_COLORS = {
  'Proposal':  { bg: 'rgba(229,192,75,0.1)',  color: '#E5C04B' },
  'Confirmed': { bg: 'rgba(83,145,255,0.1)',  color: '#5391FF' },
  'Active':    { bg: 'rgba(57,255,20,0.1)',   color: '#39FF14' },
  'Completed': { bg: 'rgba(86,86,86,0.2)',    color: '#565656' },
  'Archived':  { bg: 'rgba(40,40,40,0.8)',    color: '#3a3a3a' },
};

function RecentEventItem({ event, onClick }) {
  const [hovered, setHovered] = useState(false);
  const sc = EVENT_STATUS_COLORS[event.status] || EVENT_STATUS_COLORS['Proposal'];
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 24px 8px 36px',
        cursor: 'pointer',
        color: hovered ? '#39FF14' : '#F5F5F5',
        backgroundColor: hovered ? 'rgba(57, 255, 20, 0.06)' : 'transparent',
        transition: 'all 0.15s ease',
      }}
    >
      <span
        style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 400,
          fontSize: '14px',
          maxWidth: '160px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: 'inherit',
        }}
      >
        {event.title}
      </span>
      <span
        style={{
          backgroundColor: sc.bg,
          color: sc.color,
          border: `1px solid ${sc.color}`,
          borderRadius: '8px',
          padding: '1px 6px',
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: '10px',
          whiteSpace: 'nowrap',
          marginLeft: '8px',
          flexShrink: 0,
        }}
      >
        {event.status}
      </span>
    </div>
  );
}

function ViewAllEventsLink({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '8px 24px 8px 36px',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '13px',
        color: hovered ? '#FFFFFF' : '#39FF14',
        cursor: 'pointer',
        transition: 'color 0.15s ease',
        whiteSpace: 'nowrap',
      }}
    >
      View All Events
    </div>
  );
}

function RecentProjectItem({ project, onClick }) {
  const [hovered, setHovered] = useState(false);
  const sc = STATUS_COLORS[project.status] || STATUS_COLORS['Active'];
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 24px 8px 36px',
        cursor: 'pointer',
        color: hovered ? '#39FF14' : '#F5F5F5',
        backgroundColor: hovered ? 'rgba(57, 255, 20, 0.06)' : 'transparent',
        transition: 'all 0.15s ease',
      }}
    >
      <span
        style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 400,
          fontSize: '14px',
          maxWidth: '160px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: 'inherit',
        }}
      >
        {project.title}
      </span>
      <span
        style={{
          backgroundColor: sc.bg,
          color: sc.color,
          border: `1px solid ${sc.color}`,
          borderRadius: '8px',
          padding: '1px 6px',
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: '10px',
          whiteSpace: 'nowrap',
          marginLeft: '8px',
          flexShrink: 0,
        }}
      >
        {project.status}
      </span>
    </div>
  );
}

function ViewAllLink({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '8px 24px 8px 36px',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '13px',
        color: hovered ? '#FFFFFF' : '#39FF14',
        cursor: 'pointer',
        transition: 'color 0.15s ease',
        whiteSpace: 'nowrap',
      }}
    >
      View All Projects
    </div>
  );
}

function ExpandableRow({ label, Icon, disabled, soon, newItemTo, recentProjects, onRecentClick, onViewAll, recentEvents, onRecentEventClick, onViewAllEvents }) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  if (disabled) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 24px',
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: '15px',
          color: '#565656',
          cursor: 'default',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icon style={{ fontSize: 18, color: 'inherit' }} />
          {label}
          {soon && (
            <span
              style={{
                backgroundColor: 'rgba(86, 86, 86, 0.4)',
                color: '#565656',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '10px',
                marginLeft: '8px',
              }}
            >
              Soon
            </span>
          )}
        </span>
        <ExpandMoreOutlined style={{ fontSize: 18, color: '#565656' }} />
      </div>
    );
  }

  function renderExpandedContent() {
    // Projects row — recent projects UI
    if (recentProjects !== undefined) {
      return (
        <>
          <div
            style={{
              padding: '8px 24px 4px 36px',
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: '10px',
              color: '#565656',
              letterSpacing: '2px',
            }}
          >
            RECENT
          </div>

          {recentProjects.length === 0 ? (
            <div
              style={{
                padding: '8px 24px 8px 36px',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 300,
                fontSize: '12px',
                color: '#565656',
                fontStyle: 'italic',
              }}
            >
              No recent projects
            </div>
          ) : (
            recentProjects.map((project) => (
              <RecentProjectItem
                key={project._id}
                project={project}
                onClick={() => onRecentClick && onRecentClick(project)}
              />
            ))
          )}

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '4px 0' }} />

          <ViewAllLink onClick={onViewAll} />
        </>
      );
    }

    // Events row — recent events UI
    if (recentEvents !== undefined) {
      return (
        <>
          <div
            style={{
              padding: '8px 24px 4px 36px',
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 500,
              fontSize: '10px',
              color: '#565656',
              letterSpacing: '2px',
            }}
          >
            RECENT
          </div>

          {recentEvents.length === 0 ? (
            <div
              style={{
                padding: '8px 24px 8px 36px',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 300,
                fontSize: '12px',
                color: '#565656',
                fontStyle: 'italic',
              }}
            >
              No recent events
            </div>
          ) : (
            recentEvents.map((event) => (
              <RecentEventItem
                key={event._id}
                event={event}
                onClick={() => onRecentEventClick && onRecentEventClick(event)}
              />
            ))
          )}

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '4px 0' }} />

          <ViewAllEventsLink onClick={onViewAllEvents} />
        </>
      );
    }

    // Default rows (Proposals, etc.)
    return (
      <>
        <SubItem label="No recent items" disabled />
        <NewLink label={`+ New ${label}`} to={newItemTo} />
      </>
    );
  }

  return (
    <>
      <div
        onClick={() => setExpanded((prev) => !prev)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 24px',
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: '15px',
          color: hovered ? '#39FF14' : '#F5F5F5',
          cursor: 'pointer',
          backgroundColor: hovered ? 'rgba(57, 255, 20, 0.06)' : 'transparent',
          transition: 'all 0.15s ease',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icon style={{ fontSize: 18, color: 'inherit' }} />
          {label}
        </span>
        <ExpandMoreOutlined
          style={{
            fontSize: 18,
            color: 'inherit',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </div>

      {expanded && (
        <div style={{ backgroundColor: 'rgba(0, 0, 0, 0.15)' }}>
          {renderExpandedContent()}
        </div>
      )}
    </>
  );
}

function SubItem({ label, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '9px 24px 9px 36px',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 400,
        fontSize: '14px',
        color: disabled ? '#888888' : (hovered ? '#F5F5F5' : '#888888'),
        cursor: disabled ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </div>
  );
}

function NewLink({ label, to }) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  return (
    <div
      onClick={() => to && navigate(to)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '9px 24px 9px 36px',
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        color: hovered ? '#FFFFFF' : '#39FF14',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'color 0.15s ease',
      }}
    >
      {label}
    </div>
  );
}

export default function HamburgerMenu() {
  const { recentProjects, recentEvents } = useWorkspace();
  const { isMenuOpen, setMenuOpen } = useUI();
  const navigate = useNavigate();

  function handleRecentProjectClick(project) {
    sessionStorage.setItem('vance_select_project', project._id);
    navigate('/projects');
    setMenuOpen(false);
  }

  function handleViewAllProjects() {
    navigate('/projects');
    setMenuOpen(false);
  }

  function handleRecentEventClick(event) {
    sessionStorage.setItem('vance_select_event', event._id);
    navigate('/events');
    setMenuOpen(false);
  }

  function handleViewAllEvents() {
    navigate('/events');
    setMenuOpen(false);
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '56px',
        left: 0,
        height: 'calc(100vh - 56px)',
        width: isMenuOpen ? '240px' : '0px',
        backgroundColor: '#333333',
        borderRight: isMenuOpen ? '1px solid #565656' : 'none',
        zIndex: 900,
        overflow: 'hidden',
        transition: 'width 0.25s ease',
        whiteSpace: 'nowrap',
      }}
    >
      <div
        style={{
          width: '240px',
          padding: '24px 0',
          overflowY: 'auto',
          height: '100%',
        }}
      >
        {/* DATABASES */}
        <div style={SECTION_LABEL_STYLE}>DATABASES</div>
        <NavItem label="Clients"   Icon={PeopleOutlined}       to="/clients" />
        <NavItem label="Vendors"   Icon={StorefrontOutlined}   to="/vendors" />
        <NavItem label="Venues"    Icon={LocationCityOutlined} to="/venues" />
        <NavItem label="Personnel" Icon={BadgeOutlined}        to="/personnel" />

        <div style={DIVIDER_STYLE} />

        {/* WORKSPACE */}
        <div style={SECTION_LABEL_STYLE}>WORKSPACE</div>
        <ExpandableRow label="Insights"  Icon={InsightsOutlined}    disabled soon />
        <ExpandableRow
          label="Projects"
          Icon={FolderOutlined}
          recentProjects={recentProjects}
          onRecentClick={handleRecentProjectClick}
          onViewAll={handleViewAllProjects}
        />
        <ExpandableRow label="Proposals" Icon={DescriptionOutlined} disabled soon />
        <ExpandableRow
          label="Events"
          Icon={EventOutlined}
          recentEvents={recentEvents}
          onRecentEventClick={handleRecentEventClick}
          onViewAllEvents={handleViewAllEvents}
        />
      </div>
    </div>
  );
}
